import { clsx, type ClassValue } from 'clsx';
import { format, formatDistanceToNowStrict, parseISO } from 'date-fns';

export function cn(...v: ClassValue[]) {
  return clsx(v);
}

const toDate = (d: string | Date) => (typeof d === 'string' ? (d.length === 10 ? parseISO(d) : new Date(d)) : d);

/** Formats in the viewer's local time; pass a timezone to render in a specific zone. */
export function fmtDate(d?: string | Date | null, pattern = 'dd MMM yyyy') {
  if (!d) return '—';
  return format(toDate(d), pattern);
}

export function fmtDateTime(d?: string | Date | null, tz?: string) {
  if (!d) return '—';
  const date = toDate(d);
  if (tz) {
    return new Intl.DateTimeFormat(undefined, { timeZone: tz, day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }).format(date);
  }
  return format(date, 'dd MMM yyyy, HH:mm');
}

export function fmtTime(d?: string | Date | null, tz?: string) {
  if (!d) return '—';
  return new Intl.DateTimeFormat(undefined, { timeZone: tz, hour: '2-digit', minute: '2-digit' }).format(toDate(d));
}

export function fromNow(d?: string | Date | null) {
  if (!d) return '—';
  return formatDistanceToNowStrict(toDate(d), { addSuffix: true });
}

export function minutesToHours(min?: number | null) {
  if (!min) return '0h';
  const h = Math.floor(min / 60);
  const m = min % 60;
  return `${h}h${m ? ` ${m}m` : ''}`;
}

export function initials(name?: string | null) {
  return (name ?? '?')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]!.toUpperCase())
    .join('');
}

export function titleCase(v?: string | null) {
  if (!v) return '';
  return v
    .toLowerCase()
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

export function todayISO() {
  return format(new Date(), 'yyyy-MM-dd');
}

export function isoDate(d: Date) {
  return format(d, 'yyyy-MM-dd');
}

/** datetime-local input value <-> ISO string with offset. */
export function toLocalInput(iso?: string | null) {
  if (!iso) return '';
  return format(new Date(iso), "yyyy-MM-dd'T'HH:mm");
}
export function fromLocalInput(v?: string | null) {
  if (!v) return null;
  return new Date(v).toISOString();
}

export function uuid() {
  return crypto.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}
